import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Loader2, Flame, Clock, ArrowRight, Plus, X } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = ["chicken", "spinach", "chickpeas", "sweet potato", "eggs", "salmon", "quinoa", "avocado"];

export default function DemoTry() {
  const navigate = useNavigate();
  const [items, setItems] = useState(["chicken", "spinach", "lemon"]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [recipe, setRecipe] = useState(null);

  const add = (val) => {
    const v = (val ?? draft).trim().toLowerCase();
    if (!v || items.includes(v) || items.length >= 8) return;
    setItems([...items, v]);
    setDraft("");
  };
  const remove = (v) => setItems(items.filter((i) => i !== v));

  const cook = async () => {
    if (items.length === 0) {
      toast.error("Add at least one ingredient to try it.");
      return;
    }
    setLoading(true);
    setRecipe(null);
    try {
      const res = await api.post("/demo/recipe", { ingredients: items, meal_type: "Dinner" });
      if (!res.data?.recipe) throw new Error("No recipe");
      setRecipe(res.data.recipe);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error(err.response?.data?.detail || "Daily demo limit reached — sign up to keep cooking!");
      } else {
        toast.error("Our kitchen is busy right now. Please try again in a moment.");
      }
    } finally {
      setLoading(false);
    }
  };

  const n = recipe?.nutrition || {};

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 py-16" data-testid="demo-section">
      <div className="rounded-[2rem] border border-[#E2D9CC] bg-white overflow-hidden grid md:grid-cols-2">
        {/* Input side */}
        <div className="p-8 md:p-10">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#C85A32] font-medium">Try it — no signup</span>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-3 leading-tight">
            Type what's in your kitchen
          </h2>
          <p className="text-[#6E6259] mt-3 text-sm md:text-base">
            Add a few ingredients and watch NourishCraft invent a real, healthy recipe — right here, before you make an account.
          </p>

          <div className="flex flex-wrap gap-2 mt-6" data-testid="demo-chips">
            {items.map((i) => (
              <span key={i} className="inline-flex items-center gap-1.5 bg-[#FBF9F5] border border-[#E2D9CC] rounded-full pl-3 pr-2 py-1.5 text-sm">
                {i}
                <button onClick={() => remove(i)} data-testid={`demo-remove-${i}`} className="text-[#B6A48A] hover:text-[#C85A32]">
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
          </div>

          <div className="flex gap-2 mt-3">
            <Input
              data-testid="demo-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
              placeholder="e.g. tomatoes"
              className="rounded-xl bg-[#FBF9F5] border-[#E2D9CC] h-11 focus-visible:ring-[#C85A32]"
            />
            <Button onClick={() => add()} data-testid="demo-add-btn" variant="outline" className="rounded-xl border-[#E2D9CC] h-11 px-4">
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            {SUGGESTIONS.filter((s) => !items.includes(s)).slice(0, 5).map((s) => (
              <button key={s} onClick={() => add(s)} data-testid={`demo-suggest-${s}`} className="text-xs text-[#9C8F85] hover:text-[#C85A32] border border-dashed border-[#E2D9CC] rounded-full px-3 py-1">
                + {s}
              </button>
            ))}
          </div>

          <Button onClick={cook} disabled={loading} data-testid="demo-cook-btn" className="w-full mt-6 rounded-full bg-[#C85A32] hover:bg-[#B04B26] text-white h-12 text-base">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {loading ? "Crafting your recipe…" : "Craft a recipe"}
          </Button>
        </div>

        {/* Output side */}
        <div className="bg-[#2C221E] text-white p-8 md:p-10 flex flex-col justify-center min-h-[360px]" data-testid="demo-output">
          {!recipe && !loading && (
            <div className="text-center text-[#C5B8A5]">
              <Sparkles className="w-8 h-8 mx-auto mb-3 text-[#D49B4B]" />
              <p className="text-sm">Your AI-crafted recipe will appear here.</p>
            </div>
          )}
          {loading && (
            <div className="text-center text-[#C5B8A5] animate-pulse">
              <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-[#D49B4B]" />
              <p className="text-sm">Reading your pantry and writing a recipe…</p>
            </div>
          )}
          {recipe && !loading && (
            <div className="animate-fade-up" data-testid="demo-recipe-card">
              <div className="flex flex-wrap gap-2 mb-3">
                {(recipe.dietary_tags || []).slice(0, 3).map((t) => (
                  <span key={t} className="text-[10px] font-mono uppercase tracking-wide bg-[#4A6B4E] rounded-full px-2.5 py-1">{t}</span>
                ))}
              </div>
              <h3 className="font-serif text-2xl md:text-3xl leading-tight">{recipe.title}</h3>
              <p className="text-[#C5B8A5] text-sm mt-2">{recipe.description}</p>
              <div className="flex items-center gap-5 mt-4 text-sm text-[#E7DCCB]">
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#D49B4B]" />{(recipe.prep_time || 0) + (recipe.cook_time || 0)} min</span>
                <span className="flex items-center gap-1.5"><Flame className="w-4 h-4 text-[#D49B4B]" />{n.calories ?? "—"} kcal</span>
                <span>{recipe.servings || 2} servings</span>
              </div>
              {Array.isArray(recipe.instructions) && recipe.instructions.length > 0 && (
                <ol className="mt-4 space-y-1.5 text-sm text-[#E7DCCB] list-decimal list-inside">
                  {recipe.instructions.slice(0, 3).map((s, i) => <li key={i}>{s}</li>)}
                  {recipe.instructions.length > 3 && <li className="list-none text-[#9C8F85] italic">…and {recipe.instructions.length - 3} more steps</li>}
                </ol>
              )}
              <Button onClick={() => navigate("/login?mode=register")} data-testid="demo-signup-btn" className="group mt-6 rounded-full bg-[#C85A32] hover:bg-[#B04B26] text-white h-11 px-6">
                Save this & get 30 days free
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
